import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { useToast } from '@/components/ui/Toast';
import { extractErrorCode } from '@/lib/graphql-errors';
import { PlaceBet, type PlaceBetData } from '@/features/bets/PlaceBet';
import { GET_RACE_DETAILS, MY_BETS } from '@/graphql/queries';
import { PLACE_BET } from '@/graphql/mutations';
import { Gender } from '@/gql/graphql';

export function PlaceBetPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const { toast } = useToast();

    const [activeGender, setActiveGender] = useState<Gender>(Gender.Male);
    const [maleTokens, setMaleTokens] = useState(0);
    const [femaleTokens, setFemaleTokens] = useState(0);
    const [maleCanSubmit, setMaleCanSubmit] = useState(false);
    const [femaleCanSubmit, setFemaleCanSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const maleDataRef = useRef<(() => PlaceBetData) | null>(null);
    const femaleDataRef = useRef<(() => PlaceBetData) | null>(null);

    const { data, loading } = useQuery(GET_RACE_DETAILS, {
        variables: { id: id! },
        skip: !id,
    });
    const { data: existingBetData, loading: loadingBet } = useQuery(MY_BETS, {
        variables: { pagination: { page: 1, limit: 1 }, filter: { raceId: id } },
        skip: !id,
    });
    const [placeBet] = useMutation(PLACE_BET, { refetchQueries: ['MyBets', 'RaceBettors'] });

    const race = data?.race;
    const existingBet = existingBetData?.myBets?.items?.[0];
    const tokenBudget = race?.bettingConfig?.tokenCount ?? null;

    const availableGenders = useMemo(() => {
        const runners = race?.runners ?? [];
        const genders: Gender[] = [];
        if (runners.some(r => r.gender === Gender.Male)) genders.push(Gender.Male);
        if (runners.some(r => r.gender === Gender.Female)) genders.push(Gender.Female);
        return genders;
    }, [race?.runners]);

    const isMultiGender = availableGenders.length > 1;

    const effectiveGender = availableGenders.includes(activeGender)
        ? activeGender
        : availableGenders[0] ?? Gender.Male;

    // Extract existing rankings per gender for PlaceBet init
    const maleExistingRankings = useMemo(() =>
        (existingBet?.rankings ?? [])
            .filter(r => r.gender === Gender.Male && r.runner?.id && r.position != null)
            .map(r => ({ runnerId: r.runner!.id!, position: r.position!, tokens: r.tokens ?? 1 })),
        [existingBet],
    );
    const femaleExistingRankings = useMemo(() =>
        (existingBet?.rankings ?? [])
            .filter(r => r.gender === Gender.Female && r.runner?.id && r.position != null)
            .map(r => ({ runnerId: r.runner!.id!, position: r.position!, tokens: r.tokens ?? 1 })),
        [existingBet],
    );

    const handleMaleTokens = useCallback((count: number) => setMaleTokens(count), []);
    const handleFemaleTokens = useCallback((count: number) => setFemaleTokens(count), []);

    const handleMaleDataRef = useCallback((fn: (() => PlaceBetData) | null) => { maleDataRef.current = fn; }, []);
    const handleFemaleDataRef = useCallback((fn: (() => PlaceBetData) | null) => { femaleDataRef.current = fn; }, []);

    const canSubmit = (maleCanSubmit || femaleCanSubmit) && !isSubmitting;

    const handleSubmit = useCallback(async () => {
        if (!id) return;
        setIsSubmitting(true);
        try {
            const maleData = maleDataRef.current?.() ?? { rankings: [] };
            const femaleData = femaleDataRef.current?.() ?? { rankings: [] };
            const allRankings = [...maleData.rankings, ...femaleData.rankings];

            if (allRankings.length === 0) return;

            await placeBet({
                variables: { input: { raceId: id, rankings: allRankings } },
            });
            toast(t('success'), 'success');
            navigate(`/races/${id}`, { replace: true });
        } catch (err: unknown) {
            const code = extractErrorCode(err);
            toast(tc(`error.${code}`, { defaultValue: t('error') }), 'error');
        } finally { setIsSubmitting(false); }
    }, [id, placeBet, toast, t, tc, navigate]);

    if (!id) return null;

    if (loading || loadingBet) return (
        <Page>
            <PageHeader title={t('page.title')} backLink={`/races/${id}`} width="standard" />
            <div className="flex items-center justify-center py-20">
                <Loader2 size={28} className="animate-spin text-emerald-600" />
            </div>
        </Page>
    );

    const headerActions = (
        <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs transition-all active:scale-95",
                canSubmit
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-200 hover:bg-emerald-700"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
            )}
        >
            <Ticket size={14} />
            {isSubmitting ? t('submitting') : t('submitShort')}
        </button>
    );

    return (
        <Page>
            <PageHeader
                title={t('page.title')}
                backLink={`/races/${id}`}
                width="standard"
                actions={headerActions}
            />

            {/* Gender tabs */}
            {isMultiGender && (
                <PageContent width="standard" className="pt-2 pb-0">
                    <GenderTabs
                        genders={availableGenders}
                        active={effectiveGender}
                        onChange={setActiveGender}
                        maleTokens={maleTokens}
                        femaleTokens={femaleTokens}
                        tokenBudget={tokenBudget}
                    />
                </PageContent>
            )}

            {availableGenders.map((g) => (
                <div key={g} className={g !== effectiveGender ? 'hidden' : undefined}>
                    <PlaceBet
                        raceId={id}
                        gender={g}
                        existingRankings={g === Gender.Male ? maleExistingRankings : femaleExistingRankings}
                        otherGenderTokens={g === Gender.Male ? femaleTokens : maleTokens}
                        onTokensChange={g === Gender.Male ? handleMaleTokens : handleFemaleTokens}
                        onDataRef={g === Gender.Male ? handleMaleDataRef : handleFemaleDataRef}
                        onCanSubmitChange={g === Gender.Male ? setMaleCanSubmit : setFemaleCanSubmit}
                    />
                </div>
            ))}
        </Page>
    );
}

// --- Gender Tabs ---

function GenderTabs({ genders, active, onChange, maleTokens, femaleTokens, tokenBudget }: {
    genders: Gender[];
    active: Gender;
    onChange: (g: Gender) => void;
    maleTokens: number;
    femaleTokens: number;
    tokenBudget: number | null;
}) {
    const { t } = useTranslation('bets');

    return (
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1" role="tablist">
            {genders.map((g) => {
                const isActive = g === active;
                const label = g === Gender.Male ? t('gender.MALE') : t('gender.FEMALE');
                const tokens = g === Gender.Male ? maleTokens : femaleTokens;
                const showTokens = tokenBudget != null;

                return (
                    <button
                        key={g}
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onChange(g)}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all",
                            isActive
                                ? "bg-white shadow-sm text-slate-900"
                                : "text-slate-400 hover:text-slate-600"
                        )}
                    >
                        <span className={cn(
                            "w-2 h-2 rounded-full",
                            g === Gender.Male ? "bg-blue-500" : "bg-pink-500"
                        )} />
                        {label}
                        {showTokens && (
                            <span className="text-xs font-medium text-slate-400">({tokens})</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
